import styled from "@emotion/styled"
import { Accordion as MuiAccordion } from "@mui/material"
import AccordionDetails from "@mui/material/AccordionDetails"
import AccordionSummary from "@mui/material/AccordionSummary"
import Image from "next/image"
import { ElementType } from "react"
import { trackQuestionClicked } from "reusable/ChargeGTM"
import colors from "theme/colors"
import { NewText, NewTextProps } from "ui"
import { checkIfStartsWithParagraph } from "utils"
import { replaceHrefsWithMediaUrl } from "utils/utils"

const Accordion = styled(MuiAccordion)({
  "&.MuiAccordion-root": {
    boxShadow: "none",
    "&:before": {
      // This targets the default divider line between closed accordions
      display: "none",
    },
    "& .MuiAccordionSummary-root": {
      padding: 0,
    },
    "& .MuiAccordionDetails-root": {
      paddingTop: 0,
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
})

const StyledDetailsContainer = styled.div`
  display: flex;
  gap: 40px;
`

const StyledImageContainer = styled.div<{ arrowIconSize: number }>`
  position: relative;
  width: ${(p) => p.arrowIconSize}px;
  height: ${(p) => p.arrowIconSize}px;
`
const StyledNewText = styled(NewText)`
  p {
    margin: 0;
  }
`
const StyledTitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 80px;
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 52px;
  }

  ${(props) => props.theme.breakpoints.up("xl")} {
    gap: 24px;
  }
`

type Props = {
  index?: number,
  accordionTitle: string,
  accordionContent: string,
  titleOverrideStyle?: Partial<NewTextProps>,
  contenrOverrideStyle?: Partial<NewTextProps>,
  arrowIconSize: number,
  titleComponent?: ElementType
}

function AccordionItem({ index, accordionTitle, accordionContent, titleOverrideStyle, contenrOverrideStyle, arrowIconSize, titleComponent }: Props) {

  // Normalize any Umbraco style links inside the rich text so that '/pages' is stripped
  // and media links are resolved similarly to other rich text components.
  const normalizedContent = accordionContent
    ? replaceHrefsWithMediaUrl(accordionContent)
    : ""

  const handleAccordionChange = (event: React.SyntheticEvent, isExpanded: boolean) => {
    if (isExpanded) {
      trackQuestionClicked(accordionTitle); // Tracking accordion expand
    }
  }
  return (
    <Accordion defaultExpanded={index === 0} onChange={handleAccordionChange}>
      <AccordionSummary
        expandIcon={
          <StyledImageContainer arrowIconSize={arrowIconSize}>
            <Image src="/images/arrow-up-accordion.png" fill alt="" priority />
          </StyledImageContainer>
        }
        aria-controls="panel1a-content"
        id={accordionTitle}
        sx={{
          "& .MuiAccordionSummary-expandIconWrapper": {
            // styles for not expanded state
            transform: "rotate(90deg)",
          },
          "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
            // styles for expanded state
            transform: "rotate(180deg)",
          },
          "& .MuiAccordionSummary-content": {
            margin: "20px 0px"
          },
        }}
      >
        <StyledTitleContainer>
          <NewText
            component={titleComponent || "p"}
            largeDesktopFontSize={40}
            desktopFontWeight={500}
            largeDesktopLineHeight={"60px"}
            color={colors.black}
            mobileFontSize={20}
            mobileLineHeight={"28px"}
            desktopFontSize={30}
            desktopLineHeight="44px"
            {...titleOverrideStyle}
          >
            {accordionTitle}
          </NewText>
        </StyledTitleContainer>
      </AccordionSummary>
      <AccordionDetails>
        <StyledDetailsContainer >
          <StyledNewText
            component={checkIfStartsWithParagraph(accordionContent) ? "div" : "p"}
            largeDesktopFontSize={24}
            largeDesktopLineHeight={"33.6px"}
            desktopFontWeight={400}
            color={colors.black}
            mobileFontSize={16}
            mobileLineHeight={"24px"}
            desktopFontSize={20}
            desktopLineHeight="28.8px"
            dangerouslySetInnerHTML={{ __html: normalizedContent }}
            {...contenrOverrideStyle}
          />
        </StyledDetailsContainer>
      </AccordionDetails>
    </Accordion>
  )
}

export default AccordionItem
